import { useCallback, useState, useMemo, useRef } from "react";
import { importCalendars } from "../../../api/index.js";
import ImportCSV from "./ImportCSV.js";
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
  Grid,
  Paper,
  Stack,
  Chip,
  Divider,
  Button,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
// import ReplayIcon from "@mui/icons-material/Replay";

  /* Helper pour merger les résultats serveur partiels (batchs) en un seul objet agrégé */
    const mergeServerResult = (prev, next) => {
      if (!prev) return next;

      const p = prev.importSummary?.calendars;
      const n = next.importSummary?.calendars;
      if (!p || !n) return next;

      const merged = {
        ...next,
        message: prev.message,
        importSummary: {
          ...next.importSummary,
          calendars: {
            ...n,
            total: (p.total || 0) + (n.total || 0),
            successCount: (p.successCount || 0) + (n.successCount || 0),
            errorCount: (p.errorCount || 0) + (n.errorCount || 0),

            // lookup stats (on additionne)
            lookupStats: ["competition", "homeTeam", "awayTeam"].reduce((acc, key) => {
              const ps = p.lookupStats?.[key] || {};
              const ns = n.lookupStats?.[key] || {};

              // compat ancienne structure: found peut venir de "with-provider"
              const pFound = ps.found ?? ps["with-provider"] ?? 0;
              const nFound = ns.found ?? ns["with-provider"] ?? 0;

              acc[key] = {
                ...ns,
                found: pFound + nFound,
                notFound: (ps.notFound ?? 0) + (ns.notFound ?? 0),
              };
              return acc;
            }, {}),

            // on concat sans réécrire index (Option A)
            lines: [...(p.lines || []), ...(n.lines || [])],
          },
        },
        details: {
          successes: [...(prev.details?.successes || []), ...(next.details?.successes || [])],
          errors: [...(prev.details?.errors || []), ...(next.details?.errors || [])],
        },
      };

      const t = merged.importSummary.calendars;
      merged.message = `Réception de ${t.total} lignes. Succès: ${t.successCount}. Erreurs: ${t.errorCount}.`;

      return merged;
    };


/** ---------- Petits helpers UI ---------- */

const ModeChip = ({ mode }) => {
  const color = mode === "created" ? "success" : mode === "updated" ? "info" : "default";
  return <Chip size="small" color={color} label={mode} />;
};

const LookupChip = ({ mode }) => {
  const map = {
    "with-provider": { color: "success", label: "via provider" },
    "fallback-no-provider": { color: "warning", label: "fallback (sans provider)" },
    "not-found": { color: "error", label: "introuvable" },
    undefined: { color: "default", label: "-" },
  };
  const cfg = map[mode] || map.undefined;
  return <Chip size="small" color={cfg.color} variant="outlined" label={cfg.label} />;
};

const StrategyChip = ({ strategy }) => {
  const map = {
    "update-by-provider-and-keys": {
      color: "info",
      label: "Mise à jour (clés complètes)",
      tip: "Match trouvé par provider + thirdPartyId + thirdPartyEntityId, puis mis à jour.",
    },
    "create-from-base-by-keys": {
      color: "success",
      label: "Création (copie d’un existant)",
      tip: "Aucun match par provider ; copie du 1er match trouvé par clés, puis complété avec le payload.",
    },
    "create-no-base-but-keys-present": {
      color: "success",
      label: "Création (clés fournies)",
      tip: "Clés présentes mais aucun existant ; création directe avec ces clés.",
    },
    "create-partial-keys": {
      color: "success",
      label: "Création (clés partielles)",
      tip: "Une des deux clés manquante ; création sans tentative d’update.",
    },
    "create-no-keys": {
      color: "success",
      label: "Création (sans clé)",
      tip: "Aucune clé fournie ; création simple.",
    },
  };
  const cfg = map[strategy] || { color: "default", label: strategy || "-", tip: "" };
  return (
    <Tooltip title={cfg.tip} arrow>
      <Chip size="small" color={cfg.color} label={cfg.label} />
    </Tooltip>
  );
};

const StatCard = ({ title, value, hint, tone = "default" }) => (
  <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
    <Stack spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h5" fontWeight={700}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color={tone === "danger" ? "error.main" : "text.secondary"}>
          {hint}
        </Typography>
      )}
    </Stack>
  </Paper>
);

const SectionTitle = ({ children }) => (
  <Typography variant="h6" sx={{ mt: 3, mb: 1.5 }}>
    {children}
  </Typography>
);

/** ---------- Composant principal ---------- */

const CalendarCSV = () => {
  const [loading, setLoading] = useState(false);
  const [importComplete, setImportComplete] = useState(false);

  // Erreurs côté client (pré-validation CSV)
  const [clientErrors, setClientErrors] = useState([]);

  // Résultat côté serveur (payload complet)
  const [serverResult, setServerResult] = useState(null);

  /* merger les résultats serveur partiels (batchs)*/
    const importStartedRef = useRef(false);

  // Définition des colonnes (on marque les 2 clés match comme non requises côté front)
  const columns = useMemo(
    () => [
      { id: "provider", label: "groupe" },
      { id: "discipline", label: "discipline" },
      { id: "season", label: "saison" },

      { id: "competitionName", label: "competitionName" },
      { id: "competitionId", label: "competitionId" },
      { id: "competitionThirdPartyId", label: "competitionThirdPartyId" },

      { id: "day", label: "day" },
      { id: "dateTime", label: "dateTime" },

      { id: "homeTeamName", label: "homeTeamName" },
      { id: "homeTeamId", label: "homeTeamId" },
      { id: "homeTeamThirdPartyId", label: "homeTeamThirdPartyId" },

      { id: "awayTeamName", label: "awayTeamName" },
      { id: "awayTeamId", label: "awayTeamId" },
      { id: "awayTeamThirdPartyId", label: "awayTeamThirdPartyId" },

      // Ces deux champs sont optionnels au front (aligné avec le back)
      { id: "matchId", label: "matchId", required: false },
      { id: "matchThirdPartyId", label: "matchThirdPartyId", required: false },
    ],
    []
  );

  const requiredColumns = useMemo(
    () => columns.filter((c) => c.required !== false).map((c) => c.id),
    [columns]
  );

  const isNonEmpty = (val) => val !== null && val !== undefined && String(val).trim() !== "";

  const validateRow = (row) => {
    const missing = requiredColumns.filter((col) => !isNonEmpty(row[col]));
    return { ok: missing.length === 0, missing };
  };

  const handleDownloadJson = (data) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `import-summary-${new Date().toISOString()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleMatchImport =  useCallback(async (rows) => {
      if (!importStartedRef.current) {
        importStartedRef.current = true;
        setLoading(true);
        setImportComplete(false);
        setClientErrors([]);
        setServerResult(null);
      }

      // 1) On enlève les lignes totalement vides
      const nonEmptyRows = rows.filter((row) => Object.values(row).some(isNonEmpty));

      if (nonEmptyRows.length === 0) return;

      // 2) Validation stricte : toutes colonnes obligatoires doivent être remplies
      const invalids = [];
      const valids = [];

      nonEmptyRows.forEach((row, idx) => {
        const { ok, missing } = validateRow(row);
        if (!ok) {
          invalids.push({
            index: idx + 1,
            missing,
            row,
          });
        } else {
          valids.push(row);
        }
      });

      // 3) S’il y a des erreurs, on les liste et on poursuit avec les valides
      if (invalids.length > 0) {
        setClientErrors((prev) => [
          ...prev,
          `— Batch ${new Date().toLocaleTimeString()} —`,
          `⚠️ ${invalids.length} ligne(s) invalide(s) ignorée(s), import des ${valids.length} ligne(s) valides.`,
          ...invalids.slice(0, 20).map((it) => `Ligne ${it.index} → colonnes manquantes: ${it.missing.join(", ")}`),
        ]);
      }

      // 4) S’il ne reste rien de valide
      if (valids.length === 0) return;

      // 5) Import API
      try {
        const { data } = await importCalendars(valids); // wrapper envoie {calendars: valids}
        setServerResult((prev) => mergeServerResult(prev, data));
      } catch (error) {
            console.error(error);
            const serverData = error?.response?.data;

            if (serverData?.invalids?.length) {
              setClientErrors((prev) => [
                ...prev,
                serverData.message || "Import rejeté par le serveur.",
                ...serverData.invalids.map((it) => `Ligne ${it.index} → ${it.errors.join(" | ")}`),
              ]);
            } else {
              setClientErrors((prev) => [...prev, serverData?.message || error?.message || "Erreur lors de l'import."]);
            }
          }
        },
        [requiredColumns]
      );

    const handleImportComplete = () => {
      importStartedRef.current = false;
      setImportComplete(true);
      setLoading(false);
    };

  const calendars = serverResult?.importSummary?.calendars;
  const lines = calendars?.lines || [];
  const lookupStats = calendars?.lookupStats || {};
  const detailsErrors = serverResult?.details?.errors || [];

  const hasServerErrors = (calendars?.errorCount || 0) > 0 || detailsErrors.length > 0;

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 2 }}>
        Calendrier
      </Typography>

      <ImportCSV columns={columns} process={handleMatchImport} onComplete={handleImportComplete} />

      {loading && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Traitement en cours…</Typography>
        </Stack>
      )}

      {/* Erreurs côté client (pré-validation) */}
      {!loading && importComplete && clientErrors.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Stack spacing={1}>
            <strong>Erreurs de validation CSV (côté client)</strong>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {clientErrors.map((e, i) => (
                <li key={i}>
                  <Typography variant="body2" component="span">
                    {e}
                  </Typography>
                </li>
              ))}
            </ul>
          </Stack>
        </Alert>
      )}

      {/* Résultat serveur */}
      {!loading && importComplete && serverResult && (
        <Box sx={{ mt: 2 }}>
          <Alert severity={hasServerErrors ? "warning" : "success"}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: "100%" }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {serverResult.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Import ID (client): {new Date().toLocaleString()}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Exporter le résumé en JSON">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CloudDownloadIcon />}
                    onClick={() => handleDownloadJson(serverResult)}
                  >
                    Export JSON
                  </Button>
                </Tooltip>
              </Stack>
            </Stack>
          </Alert>

          {/* Cartes de stats */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Total lignes" value={calendars?.total ?? "-"} hint={calendars?.name} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Succès" value={calendars?.successCount ?? 0} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Erreurs"
                value={calendars?.errorCount ?? 0}
                tone="danger"
                hint={detailsErrors.length ? `${detailsErrors.length} détaillée(s)` : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard title="Lignes détaillées" value={lines.length} hint="created/updated" />
            </Grid>
          </Grid>

          {/* Mini-légende fonctionnelle */}
          <Paper elevation={0} sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: "#fafafa" }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Astuce :</strong> la “Stratégie” indique comment le match a été traité :
              “Mise à jour (clés complètes)” = update via provider+clés ; “Création (copie d’un
              existant)” = copie d’un match existant avec mêmes clés ; “Création (clés fournies /
              partielles / sans clé)” = création directe.
            </Typography>
          </Paper>

          {/* Lookup stats */}
          <SectionTitle>Résolution des entités (lookup)</SectionTitle>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px dashed #e0e0e0" }}>
            <Grid container spacing={2}>
              {["competition", "homeTeam", "awayTeam"].map((key) => {
                  const s = lookupStats[key] || {};
                  // compat backend (ancienne structure) : on mappe 'found' <- 'with-provider'
                  const found = s.found ?? s["with-provider"] ?? 0;
                  const notFound = s.notFound ?? 0;
                  return (
                    <Grid item xs={12} md={4} key={key}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {key}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LookupChip mode="with-provider" />
                        <Typography variant="body2">{found}</Typography>
                        <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
                        <LookupChip mode="not-found" />
                        <Typography variant="body2">{notFound}</Typography>
                      </Stack>
                    </Grid>
                  );
                })}
            </Grid>
          </Paper>

          {/* Détail par ligne */}
          <SectionTitle>Détail par ligne</SectionTitle>
          <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid #eee" }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: "#fafafa" }}>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Stratégie</TableCell>
                  <TableCell>Match key</TableCell>
                  <TableCell>Competition</TableCell>
                  <TableCell>Home team</TableCell>
                  <TableCell>Away team</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, i) => {
                  const kp = line?.uniqueKey?.provider || "-";
                  const ktp = line?.uniqueKey?.thirdPartyId || "—";
                  const kte = line?.uniqueKey?.thirdPartyEntityId || "—";
                  return (
                    <TableRow key={`${i}-${line.matchDbId ?? line?.uniqueKey?.thirdPartyId ?? "no-id"}`}>
                       <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <ModeChip mode={line.mode} />
                      </TableCell>
                      <TableCell>
                        <StrategyChip strategy={line.keyStrategy} />
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0}>
                          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                            {kp} / {ktp} / {kte}
                          </Typography>
                          {line.matchDbId && (
                            <Typography variant="caption" color="text.secondary">
                              _id: {line.matchDbId}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">{line?.competition?.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            provider: {line?.competition?.provider ?? "-"}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <LookupChip mode={line?.competition?.lookupMode} />
                            <Typography variant="caption" color="text.secondary">
                              {line?.competition?.thirdPartyId}/{line?.competition?.thirdPartyEntityId}
                            </Typography>
                          </Stack>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">{line?.homeTeam?.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            provider: {line?.homeTeam?.provider ?? "-"}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <LookupChip mode={line?.homeTeam?.lookupMode} />
                            <Typography variant="caption" color="text.secondary">
                              {line?.homeTeam?.thirdPartyId}/{line?.homeTeam?.thirdPartyEntityId}
                            </Typography>
                          </Stack>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">{line?.awayTeam?.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            provider: {line?.awayTeam?.provider ?? "-"}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <LookupChip mode={line?.awayTeam?.lookupMode} />
                            <Typography variant="caption" color="text.secondary">
                              {line?.awayTeam?.thirdPartyId}/{line?.awayTeam?.thirdPartyEntityId}
                            </Typography>
                          </Stack>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {lines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary">
                        Aucun détail de ligne renvoyé par le serveur.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          {/* Panneau erreurs serveur (si présent) */}
          {hasServerErrors && (
            <>
              <SectionTitle>Erreurs serveur</SectionTitle>
              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2">{detailsErrors.length} erreur(s) détaillée(s)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {detailsErrors.map((e, i) => (
                      <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          Ligne {e.index}
                        </Typography>
                        <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                          {e.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", fontFamily: "monospace", whiteSpace: "pre-wrap" }}
                        >
                          {JSON.stringify(e.row, null, 2)}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CalendarCSV;
